// 定义动画配置常量
const ANIMATION_CONFIG = {
  DISTANCE: 20,
  EASING: 'ease-out',
  // 定义速度档位配置
  SPEED_TIERS: [
    {
      threshold: 4, // 超快速
      duration: 0,
      easing: 'cubic-bezier(0.19, 1, 0.22, 1)',
    },
    {
      threshold: 3,
      duration: 100,
      easing: 'cubic-bezier(0.19, 1, 0.22, 1)',
    },
    {
      threshold: 2, // 快速
      duration: 150,
      easing: 'cubic-bezier(0.19, 1, 0.22, 1)',
    },
    {
      threshold: 1, // 快速
      duration: 350,
      easing: 'cubic-bezier(0.19, 1, 0.22, 1)',
    },
    {
      threshold: 0, // 默认
      duration: 500,
      easing: 'ease-out',
    },
  ],
} as const

class SlideInHandler {
  private animationMap = new WeakMap<HTMLElement, Animation>()
  private lastScrollTime = 0
  private lastScrollTop = 0
  private scrollSpeed = 0

  constructor() {
    // 只在浏览器环境中添加滚动监听
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.handleScroll.bind(this), true)
    }
  }

  private handleScroll = (e: Event) => {
    if (!(e.target instanceof Element)) {
      return
    }

    const currentTime = Date.now()
    const currentScrollTop = e.target.scrollTop
    const timeDiff = currentTime - this.lastScrollTime

    if (timeDiff > 0) {
      // 计算滚动速度（像素/毫秒）
      this.scrollSpeed = Math.abs(currentScrollTop - this.lastScrollTop) / timeDiff
    }

    this.lastScrollTime = currentTime
    this.lastScrollTop = currentScrollTop
  }

  private calculateAnimationParams(): { duration: number, easing: string } {
    // 根据当前滚动速度找到对应的档位配置
    const tier = ANIMATION_CONFIG.SPEED_TIERS.find(
      tier => this.scrollSpeed >= tier.threshold,
    ) || ANIMATION_CONFIG.SPEED_TIERS[ANIMATION_CONFIG.SPEED_TIERS.length - 1]

    return {
      duration: tier.duration,
      easing: tier.easing,
    }
  }

  private observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && entry.boundingClientRect.top > entry.rootBounds!.top) {
        const animation = this.animationMap.get(entry.target as HTMLElement)
        if (animation) {
          setTimeout(() => {
            animation.play()
          }, 0)

          this.observer.unobserve(entry.target)
        }
      }
    })
  })

  private isBelowViewport(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect()
    return rect.top > window.innerHeight
  }

  private createSlideInAnimation(el: HTMLElement): Animation {
    const { duration, easing } = this.calculateAnimationParams()

    const animation = el.animate(
      [
        {
          transform: `translateY(${ANIMATION_CONFIG.DISTANCE}px)`,
          opacity: 0,
          willChange: 'transform, opacity',
        },
        {
          transform: 'translateY(0px)',
          opacity: 1,
          willChange: 'auto',
        },
      ],
      {
        duration,
        easing,
        fill: 'forwards',
      },
    )

    // 动画结束后清理内联样式并强制重绘
    animation.onfinish = () => {
      const propertiesToRemove = ['opacity', 'transform', 'will-change']
      propertiesToRemove.forEach((property) => {
        el.style.removeProperty(property)
      })

      // 强制重绘
      void el.offsetWidth
    }

    return animation
  }

  mounted(el: HTMLElement) {
    // 确保只在浏览器环境中执行
    if (typeof window === 'undefined') {
      return
    }

    if (!this.isBelowViewport(el)) {
      return
    }

    const animation = this.createSlideInAnimation(el)
    animation.pause()

    // 设置初始状态
    el.style.opacity = '0'
    el.style.transform = `translateY(${ANIMATION_CONFIG.DISTANCE}px)`

    this.observer.observe(el)
    this.animationMap.set(el, animation)
  }

  unmounted(el: HTMLElement) {
    this.observer.unobserve(el)
    this.animationMap.delete(el)
  }

  // 清理方法
  destroy() {
    window.removeEventListener('scroll', this.handleScroll)
  }
}

// 创建一个单例实例，但只在浏览器环境中
const handler = typeof window !== 'undefined' ? new SlideInHandler() : null

// 导出指令对象
export const vSlideIn = {
  mounted(el: HTMLElement) {
    if (handler) {
      handler.mounted(el)
    }
  },
  unmounted(el: HTMLElement) {
    if (handler) {
      handler.unmounted(el)
    }
  },
}
